import { ArrowLeft, Save, X, Trash2, CheckCircle2, AlertCircle } from 'lucide-react';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useNavigate, useParams } from 'react-router-dom';

import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';
import { Button } from '../../components/ui/Button';
import { useSidebar } from '../../context/SidebarContext';
import { useWaiverTemplate, useSaveTemplateFields } from '../../hooks/useWaivers';
import { getWaiverFileUrl } from '../../services/damageWaivers';
import type { DamageWaiverTemplateFieldInsert, WaiverFieldType, WaiverTemplateFieldMeta } from '../../types/damageWaiver.types';

// Set up PDF.js worker - use unpkg with proper path structure
// react-pdf bundles pdfjs-dist 5.4.296
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Local type for editing fields without template_id
type FieldEditData = Omit<DamageWaiverTemplateFieldInsert, 'template_id'> & {
  id?: string;
  optional?: boolean;
};

// Available field types with their auto-fill sources
const FIELD_TYPES: Array<{
  type: WaiverFieldType;
  label: string;
  icon: string;
  defaultAutoFill?: WaiverTemplateFieldMeta['autoFillSource'];
  defaultWidth: number;
  defaultHeight: number;
}> = [
  { type: 'signature', label: 'Signature', icon: '✍️', defaultWidth: 200, defaultHeight: 60 },
  { type: 'initials', label: 'Initials', icon: '✒️', defaultWidth: 100, defaultHeight: 30 },
  { type: 'signer_name', label: 'Signer Name', icon: '✍️', defaultAutoFill: 'signer_name', defaultWidth: 200, defaultHeight: 30 },
  { type: 'student_name', label: 'Student Name', icon: '👤', defaultAutoFill: 'student_name', defaultWidth: 200, defaultHeight: 30 },
  { type: 'email', label: 'Email', icon: '📧', defaultAutoFill: 'user_email', defaultWidth: 200, defaultHeight: 30 },
  { type: 'phone', label: 'Phone', icon: '📞', defaultAutoFill: 'user_phone', defaultWidth: 150, defaultHeight: 30 },
  { type: 'date', label: 'Date', icon: '📅', defaultAutoFill: 'current_date', defaultWidth: 120, defaultHeight: 30 },
  { type: 'text', label: 'Text Field', icon: '📝', defaultWidth: 150, defaultHeight: 30 },
];

export default function TemplateFieldsPage() {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const { data: template, isLoading } = useWaiverTemplate(templateId);
  const saveFieldsMutation = useSaveTemplateFields();
  const { isOpen: isSidebarOpen } = useSidebar();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [userSetScale, setUserSetScale] = useState<number | null>(null); // Track if user manually set zoom
  const [pdfPageDimensions, setPdfPageDimensions] = useState({ width: 612, height: 792 }); // Base PDF dimensions (always 612x792)
  const [fields, setFields] = useState<FieldEditData[]>([]);
  const [selectedField, setSelectedField] = useState<FieldEditData | null>(null);
  const [draggedFieldType, setDraggedFieldType] = useState<WaiverFieldType | null>(null);
  const [isDraggingOnCanvas, setIsDraggingOnCanvas] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  
  const canvasRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const prevSidebarOpenRef = useRef<boolean>(false);
  
  // Track mouse/touch interactions for tap vs drag detection
  const mouseDownTimeRef = useRef<number>(0);
  const mouseDownLocationRef = useRef<{ x: number; y: number } | null>(null);
  const hasMovedRef = useRef<boolean>(false);
  const isDraggingFieldRef = useRef<FieldEditData | null>(null);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Track scroll position
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      scrollPositionRef.current = container.scrollTop;
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Only restore scroll position when sidebar opens/closes, not during field operations
  useEffect(() => {
    const isSidebarOpen = selectedField !== null;
    const wasSidebarOpen = prevSidebarOpenRef.current;
    
    // Only restore if sidebar state actually changed (open -> closed or closed -> open)
    if (isSidebarOpen !== wasSidebarOpen) {
      prevSidebarOpenRef.current = isSidebarOpen;
      
      // Save current scroll position before sidebar state changes
      if (scrollContainerRef.current) {
        scrollPositionRef.current = scrollContainerRef.current.scrollTop;
      }
      
      const container = scrollContainerRef.current;
      if (!container) return;

      // Use requestAnimationFrame to restore after render completes
      window.requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
      });
    }
  }, [selectedField]);

  // Load PDF URL - fetch as blob to handle authentication properly
  useEffect(() => {
    if (template?.storage_path) {
      // Loading PDF from storage path
      
      let blobUrl: string | null = null;
      
      // Fetch the PDF as a blob with authentication
      const loadPdf = async () => {
        try {
          // Get signed URL
          const signedUrl = await getWaiverFileUrl(template.storage_path!, 3600);
          // PDF signed URL generated
          
          // Fetch the PDF - signed URL already contains auth token in query string
          const response = await fetch(signedUrl, {
            method: 'GET',
          });
          
          if (!response.ok) {
            throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
          }
          
          // Create blob URL for react-pdf
          const blob = await response.blob();
          blobUrl = URL.createObjectURL(blob);
          setPdfUrl(blobUrl);
        } catch (err: unknown) {
          console.error('Failed to load PDF:', err);
          showNotification('error', `Failed to load PDF preview: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
      };
      
      loadPdf();
      
      // Cleanup: revoke blob URL when component unmounts or template changes
      return () => {
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
        }
      };
    }
  }, [template?.storage_path]);

  // Load existing fields
  useEffect(() => {
    if (template?.fields) {
      const loadedFields = template.fields.map(f => ({
        id: f.id,
        field_type: f.field_type,
        x: f.x,
        y: f.y,
        width: f.width,
        height: f.height,
        meta: f.meta,
        optional: f.optional ?? false,
        order: f.order,
      }));
      
      setFields(loadedFields);
    }
  }, [template]);

  // Memoize PDF.js options to prevent unnecessary reloads
  // Use unpkg for consistency with worker
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  }), []);

  // Handle PDF document load success
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  // Debug: Log scale changes
  useEffect(() => {
    // Scale effect for debugging
  }, [scale]);

  // Calculate responsive scale for all screen sizes - PDF stays at base width, container scales
  useEffect(() => {
    let resizeTimeout: ReturnType<typeof setTimeout>;
    
    // Calculate initial scale once, synchronously
    const calculateInitialScale = () => {
      if (!scrollContainerRef.current) return;
      
      const container = scrollContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const containerPadding = window.innerWidth < 768 ? 16 : 32;
      const availableWidth = containerRect.width - containerPadding;
      const basePdfWidth = 612;
      const calculatedScale = (availableWidth - 8) / basePdfWidth;
      // Apply 0.75 multiplier to show more of the PDF without scrolling
      const scaledDown = calculatedScale * 0.75;
      const clampedScale = Math.max(0.3, Math.min(2, scaledDown));
      
      if (window.innerWidth < 768) {
        setScale(clampedScale);
        setUserSetScale(null);
      } else if (userSetScale === null) {
        setScale(Math.max(0.5, Math.min(2, clampedScale)));
      }
    };
    
    // Debounced handler for resize events only
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (!scrollContainerRef.current) return;
        
        const container = scrollContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const containerPadding = window.innerWidth < 768 ? 16 : 32;
        const availableWidth = containerRect.width - containerPadding;
        const basePdfWidth = 612;
        const calculatedScale = (availableWidth - 8) / basePdfWidth;
        // Apply 0.75 multiplier to show more of the PDF without scrolling
        const scaledDown = calculatedScale * 0.75;
        const clampedScale = Math.max(0.3, Math.min(2, scaledDown));
        
        if (window.innerWidth < 768) {
          setScale(clampedScale);
          setUserSetScale(null);
        } else if (userSetScale === null) {
          setScale(Math.max(0.5, Math.min(2, clampedScale)));
        }
      }, 100);
    };

    // Calculate initial scale once
    calculateInitialScale();
    // Then listen for resize events
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [userSetScale]); // Recalculate when user overrides scale

  // Handle PDF page render success - get actual rendered dimensions
  const onPageRenderSuccess = useCallback((_page: { width: number; height: number; pageNumber: number }) => {
    // PDF renders at base size (612x792 points = pixels)
    // Fields coordinates are stored in PDF points, so we always use base dimensions
    setPdfPageDimensions({ width: 612, height: 792 });
    
    // CRITICAL: Check if there's any offset between the canvas and the fields overlay
    setTimeout(() => {
      if (canvasRef.current) {
        const canvas = canvasRef.current.querySelector('canvas');
        const pageDiv = canvasRef.current.querySelector('.react-pdf__Page');
        const fieldsOverlay = canvasRef.current.querySelector('.fields-overlay-debug');
        
        if (canvas && pageDiv && fieldsOverlay) {
          const containerRect = canvasRef.current.getBoundingClientRect();
          const canvasRect = canvas.getBoundingClientRect();
          const overlayRect = fieldsOverlay.getBoundingClientRect();
          
          // Calculate offsets in SCREEN pixels (before accounting for scale)
          const canvasScreenOffsetX = canvasRect.left - containerRect.left;
          const canvasScreenOffsetY = canvasRect.top - containerRect.top;
          const overlayScreenOffsetX = overlayRect.left - containerRect.left;
          const overlayScreenOffsetY = overlayRect.top - containerRect.top;
          
          // Calculate offsets in PDF coordinates (divide by scale to get internal coordinates)
          const canvasPdfOffsetX = canvasScreenOffsetX / scale;
          const canvasPdfOffsetY = canvasScreenOffsetY / scale;
          const overlayPdfOffsetX = overlayScreenOffsetX / scale;
          const overlayPdfOffsetY = overlayScreenOffsetY / scale;
          
          
          // If canvas and overlay have different offsets, that's our problem!
          const offsetMismatchX = Math.abs(canvasPdfOffsetX - overlayPdfOffsetX);
          const offsetMismatchY = Math.abs(canvasPdfOffsetY - overlayPdfOffsetY);
          if (offsetMismatchX > 1 || offsetMismatchY > 1) {
            console.error('🚨 OFFSET MISMATCH DETECTED! Canvas and overlay are not aligned!');
            console.error('Mismatch X:', offsetMismatchX, 'px');
            console.error('Mismatch Y:', offsetMismatchY, 'px');
          }
        }
      }
    }, 100);
    
    // Trigger scale recalculation after PDF loads (only if user hasn't set zoom)
    if (scrollContainerRef.current && userSetScale === null) {
      const containerRect = scrollContainerRef.current.getBoundingClientRect();
      const containerPadding = window.innerWidth < 768 ? 16 : 32;
      const availableWidth = containerRect.width - containerPadding;
      const calculatedScale = (availableWidth - 8) / 612;
      const scaledDown = calculatedScale * 0.75;
      
      if (window.innerWidth < 768) {
        setScale(Math.max(0.3, Math.min(2, scaledDown)));
      } else {
        // Auto-fit on desktop if user hasn't set zoom
        setScale(Math.max(0.5, Math.min(2, scaledDown)));
      }
    }
  }, [scale, userSetScale]);

  // Handle touch events for mobile drag-and-drop
  const handleTouchStart = useCallback((fieldType: WaiverFieldType, e: React.TouchEvent) => {
    e.preventDefault();
    setDraggedFieldType(fieldType);
  }, []);

  const handleTouchEnd = useCallback(() => {
    // On mobile, we'll place on tap instead of drag
    setDraggedFieldType(null);
  }, []);

  // Handle touch/click on canvas for mobile placement
  const handleCanvasTouchEnd = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!draggedFieldType || !canvasRef.current) return;

    const touch = e.changedTouches[0];
    const rect = canvasRef.current.getBoundingClientRect();
    // Convert screen coordinates to PDF coordinates (points)
    // Container is 612px wide (same as PDF points), scaled by CSS transform
    // Screen coords -> container coords (divide by scale) = PDF coords
    const x = (touch.clientX - rect.left) / scale;
    const y = (touch.clientY - rect.top) / scale;

    const fieldType = FIELD_TYPES.find(ft => ft.type === draggedFieldType);
    if (!fieldType) {
      setDraggedFieldType(null);
      return;
    }

    const newField: FieldEditData = {
      field_type: draggedFieldType,
      x: Math.max(0, x),
      y: Math.max(0, y),
      width: fieldType.defaultWidth,
      height: fieldType.defaultHeight,
      optional: false,
      meta: {
        label: fieldType.label,
        autoFillSource: fieldType.defaultAutoFill,
      },
      order: fields.length,
    };

    setFields([...fields, newField]);
    // Don't auto-open details panel - let user position field first, then click to edit
    setDraggedFieldType(null);
  }, [draggedFieldType, fields, scale]);

  // Handle canvas drag over - allow drop
  const handleCanvasDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOnCanvas(true);
  }, []);

  // Handle canvas drop - place field at drop location
  const handleCanvasDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOnCanvas(false);

    if (!draggedFieldType || !canvasRef.current) {
      setDraggedFieldType(null);
      return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    // Convert screen coordinates to PDF coordinates (points)
    // Container is 612px wide (same as PDF points), scaled by CSS transform
    // Screen coords -> container coords (divide by scale) = PDF coords
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;

    const fieldType = FIELD_TYPES.find(ft => ft.type === draggedFieldType);
    if (!fieldType) {
      setDraggedFieldType(null);
      return;
    }

    const newField: FieldEditData = {
      field_type: draggedFieldType,
      x: Math.max(0, x),
      y: Math.max(0, y),
      width: fieldType.defaultWidth,
      height: fieldType.defaultHeight,
      optional: false,
      meta: {
        label: fieldType.label,
        autoFillSource: fieldType.defaultAutoFill,
      },
      order: fields.length,
    };

    setFields([...fields, newField]);
    // Don't auto-open details panel - let user position field first, then click to edit
    setDraggedFieldType(null);
  }, [draggedFieldType, fields, scale]);

  // Handle canvas click to place field
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!draggedFieldType || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    // Convert screen coordinates to PDF coordinates (points)
    // CRITICAL: Container is 612px wide, scaled by CSS transform
    // Screen click -> container coords (accounting for scale) -> PDF coords
    // Container coords = (screenX - rect.left) / scale
    // PDF coords = container coords (since container is 612px = PDF is 612 points)
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;

    const fieldType = FIELD_TYPES.find(ft => ft.type === draggedFieldType);
    if (!fieldType) return;

    const newField: FieldEditData = {
      field_type: draggedFieldType,
      x: Math.max(0, x),
      y: Math.max(0, y),
      width: fieldType.defaultWidth,
      height: fieldType.defaultHeight,
      optional: false,
      meta: {
        label: fieldType.label,
        autoFillSource: fieldType.defaultAutoFill,
      },
      order: fields.length,
    };

    setFields([...fields, newField]);
    // Don't auto-open details panel - let user position field first, then click to edit
    setDraggedFieldType(null);
  }, [draggedFieldType, fields, scale]);

  // Handle field drag (mouse)
  const handleFieldDrag = useCallback((field: FieldEditData, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canvasRef.current) return;

    // Track start time and location for tap vs drag detection
    mouseDownTimeRef.current = Date.now();
    mouseDownLocationRef.current = { x: e.clientX, y: e.clientY };
    hasMovedRef.current = false;
    isDraggingFieldRef.current = field;
    
    // Clear selection when drag starts to prevent modal from opening
    setSelectedField(null);

    const rect = canvasRef.current.getBoundingClientRect();
    // Convert screen coordinates to PDF coordinates (points)
    // Container is 612px wide (same as PDF points), scaled by CSS transform
    // Screen coords -> container coords (divide by scale) = PDF coords
    const fieldStartX = field.x;
    const fieldStartY = field.y;
    const mouseStartX = (e.clientX - rect.left) / scale;
    const mouseStartY = (e.clientY - rect.top) / scale;
    const offsetX = mouseStartX - fieldStartX;
    const offsetY = mouseStartY - fieldStartY;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      // Check if mouse has moved significantly (more than 5px)
      if (mouseDownLocationRef.current) {
        const moveDistance = Math.sqrt(
          Math.pow(moveEvent.clientX - mouseDownLocationRef.current.x, 2) +
          Math.pow(moveEvent.clientY - mouseDownLocationRef.current.y, 2)
        );
        if (moveDistance > 5) {
          hasMovedRef.current = true;
        }
      }
      
      const newX = (moveEvent.clientX - rect.left) / scale - offsetX;
      const newY = (moveEvent.clientY - rect.top) / scale - offsetY;

      setFields(fields.map(f => {
        if (f === field || (f.id && field.id && f.id === field.id)) {
          return {
            ...f,
            x: Math.max(0, newX),
            y: Math.max(0, newY),
          };
        }
        return f;
      }));
    };

    const handleMouseUp = () => {
      const touchDuration = Date.now() - mouseDownTimeRef.current;
      const wasQuickTap = touchDuration < 300; // Less than 300ms is a quick tap
      const wasQuickRelease = !hasMovedRef.current && wasQuickTap;
      
      // Only open modal if it was a quick tap without movement
      if (wasQuickRelease && isDraggingFieldRef.current === field) {
        setSelectedField(field);
      }
      
      // Reset tracking
      mouseDownTimeRef.current = 0;
      mouseDownLocationRef.current = null;
      hasMovedRef.current = false;
      isDraggingFieldRef.current = null;
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [fields, scale]);

  // Handle field drag (touch) for mobile
  const handleFieldTouchStart = useCallback((field: FieldEditData, e: React.TouchEvent) => {
    e.stopPropagation();
    if (!canvasRef.current) return;

    const touch = e.touches[0];
    
    // Track start time and location for tap vs drag detection
    mouseDownTimeRef.current = Date.now();
    mouseDownLocationRef.current = { x: touch.clientX, y: touch.clientY };
    hasMovedRef.current = false;
    isDraggingFieldRef.current = field;
    
    // Clear selection when drag starts to prevent modal from opening
    setSelectedField(null);

    const rect = canvasRef.current.getBoundingClientRect();
    // Convert screen coordinates to PDF coordinates (points)
    // Container is 612px wide (same as PDF points), scaled by CSS transform
    // Screen coords -> container coords (divide by scale) = PDF coords
    const fieldStartX = field.x;
    const fieldStartY = field.y;
    const touchStartX = (touch.clientX - rect.left) / scale;
    const touchStartY = (touch.clientY - rect.top) / scale;
    const offsetX = touchStartX - fieldStartX;
    const offsetY = touchStartY - fieldStartY;

    const handleTouchMove = (moveEvent: TouchEvent) => {
      if (moveEvent.touches.length === 0) return;
      const moveTouch = moveEvent.touches[0];
      
      // Check if touch has moved significantly (more than 5px)
      if (mouseDownLocationRef.current) {
        const moveDistance = Math.sqrt(
          Math.pow(moveTouch.clientX - mouseDownLocationRef.current.x, 2) +
          Math.pow(moveTouch.clientY - mouseDownLocationRef.current.y, 2)
        );
        if (moveDistance > 5) {
          hasMovedRef.current = true;
        }
      }
      
      const newX = (moveTouch.clientX - rect.left) / scale - offsetX;
      const newY = (moveTouch.clientY - rect.top) / scale - offsetY;

      setFields(fields.map(f => {
        if (f === field || (f.id && field.id && f.id === field.id)) {
          return {
            ...f,
            x: Math.max(0, newX),
            y: Math.max(0, newY),
          };
        }
        return f;
      }));
    };

    const handleTouchEnd = () => {
      const touchDuration = Date.now() - mouseDownTimeRef.current;
      const wasQuickTap = touchDuration < 300; // Less than 300ms is a quick tap
      const wasQuickRelease = !hasMovedRef.current && wasQuickTap;
      
      // Only open modal if it was a quick tap without movement
      if (wasQuickRelease && isDraggingFieldRef.current === field) {
        setSelectedField(field);
      }
      
      // Reset tracking
      mouseDownTimeRef.current = 0;
      mouseDownLocationRef.current = null;
      hasMovedRef.current = false;
      isDraggingFieldRef.current = null;
      
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  }, [fields, scale]);

  // Handle field resize
  const handleFieldResize = useCallback((field: FieldEditData, direction: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault(); // Prevent default to avoid text selection
    if (!canvasRef.current) return;

    // Clear selection when resize starts to prevent modal from opening
    setSelectedField(null);

    // Convert screen coordinates to PDF coordinates (points)
    // Container is 612px wide (same as PDF points), scaled by CSS transform
    // Screen coords -> container coords (divide by scale) = PDF coords
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = field.width;
    const startHeight = field.height;
    const startFieldX = field.x;
    const startFieldY = field.y;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault(); // Prevent default during resize
      // Convert delta movement to PDF coordinates
      const deltaX = (moveEvent.clientX - startX) / scale;
      const deltaY = (moveEvent.clientY - startY) / scale;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newX = startFieldX;
      let newY = startFieldY;

      if (direction.includes('e')) {
        newWidth = Math.max(50, startWidth + deltaX);
      }
      if (direction.includes('w')) {
        newWidth = Math.max(50, startWidth - deltaX);
        newX = startFieldX + deltaX;
      }
      if (direction.includes('s')) {
        newHeight = Math.max(20, startHeight + deltaY);
      }
      if (direction.includes('n')) {
        newHeight = Math.max(20, startHeight - deltaY);
        newY = startFieldY + deltaY;
      }

      setFields(fields.map(f => {
        if (f === field || (f.id && field.id && f.id === field.id)) {
          return { ...f, width: newWidth, height: newHeight, x: newX, y: newY };
        }
        return f;
      }));
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [fields, scale]);

  // Handle save
  const handleSave = async () => {
    if (!templateId) return;

    try {
      const fieldsToSave = fields.map((f, i) => ({ ...f, order: i }));
      
      await saveFieldsMutation.save({
        templateId,
        fields: fieldsToSave,
      });
      showNotification('success', 'Fields saved successfully!');
      setTimeout(() => navigate('/damage-waivers'), 1500);
    } catch (error) {
      showNotification('error', `Failed to save fields: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Handle closing field details panel - ensure changes are synced
  const handleCloseFieldDetails = useCallback(() => {
    if (selectedField) {
      // Ensure any pending changes in selectedField are synced to fields array
      // (Changes are already applied immediately, but this ensures consistency)
      setFields(fields.map(f => 
        (f === selectedField || (f.id && selectedField.id && f.id === selectedField.id)) 
          ? selectedField 
          : f
      ));
    }
    setSelectedField(null);
  }, [selectedField, fields]);

  // Handle delete field
  const handleDeleteField = (field: FieldEditData) => {
    setFields(fields.filter(f => f !== field && !(f.id && field.id && f.id === field.id)));
    setSelectedField(null);
  };

  if (isLoading) {
    return (
      <AuthenticatedLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421]" />
        </div>
      </AuthenticatedLayout>
    );
  }

  if (!template) {
    return (
      <AuthenticatedLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-red-400">Template not found.</p>
          <Button onClick={() => navigate('/damage-waivers')}>Go Back</Button>
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <div className="h-screen flex flex-col bg-[#111010]">
        {/* Notification Toast */}
        {notification && (
          <div
            className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg ${
              notification.type === 'success'
                ? 'bg-[#9B1421] text-white'
                : 'bg-[#9B1421] text-white'
            }`}
          >
            {notification.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="font-medium">{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 hover:opacity-70 transition-opacity text-white"
            >
              ×
            </button>
          </div>
        )}

        {/* Header - Fixed at top, matches Dashboard styling */}
        <div 
          className={`fixed top-0 z-30 bg-[#111010] border-b border-white/10 transition-all duration-300 ease-in-out ${
            isSidebarOpen ? 'left-0 md:left-64' : 'left-0 md:left-16'
          } ${selectedField ? 'right-0 md:right-80' : 'right-0'}`}
        >
          <div className="px-4 sm:px-6 lg:px-8 py-4 w-full">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <Button
                  onClick={() => navigate('/damage-waivers')}
                  variant="outline"
                  className="border-[#504A4A] text-[#A9A3A3] hover:bg-[#363232] flex-shrink-0"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <div className="min-w-0 flex-1">
                  <h1 className="text-text-primary font-heading text-2xl md:text-4xl font-bold truncate">{template.name}</h1>
                  <p className="text-white/80 mt-1">Configure fields for your waiver template</p>
                </div>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                {numPages > 1 && (
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
                      disabled={pageNumber === 1}
                      className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] px-3 py-1 text-sm disabled:opacity-50"
                    >
                      Previous
                    </Button>
                    <span className="text-[#A9A3A3] text-sm">
                      Page {pageNumber} of {numPages}
                    </span>
                    <Button
                      onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
                      disabled={pageNumber === numPages}
                      className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] px-3 py-1 text-sm disabled:opacity-50"
                    >
                      Next
                    </Button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => {
                      const newScale = Math.max(0.5, scale - 0.25);
                      setScale(newScale);
                      setUserSetScale(newScale); // Mark as user-set
                    }}
                    className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] px-3 py-1 text-sm"
                  >
                    −
                  </Button>
                  <span className="text-[#A9A3A3] text-sm w-16 text-center">{Math.round(scale * 100)}%</span>
                  <Button
                    onClick={() => {
                      const newScale = Math.min(2, scale + 0.25);
                      setScale(newScale);
                      setUserSetScale(newScale); // Mark as user-set
                    }}
                    className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] px-3 py-1 text-sm"
                  >
                    +
                  </Button>
                </div>
                <Button
                  onClick={handleSave}
                  disabled={saveFieldsMutation.isLoading}
                  className="bg-[#9B1421] hover:bg-[#7A0F1A] text-white px-6 py-2 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saveFieldsMutation.isLoading ? 'Saving...' : 'Save Fields'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content - Add padding-top to account for fixed header */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden pt-[140px] sm:pt-[120px]">
          {/* Mobile: Horizontal Field Types Bar */}
          <div className="md:hidden bg-[#221F1F] border-b border-[#504A4A] overflow-x-auto p-2 shrink-0">
            <div className="flex gap-2 min-w-max">
              {FIELD_TYPES.map((fieldType) => (
                <div
                  key={fieldType.type}
                  draggable
                  onDragStart={() => setDraggedFieldType(fieldType.type)}
                  onDragEnd={() => setDraggedFieldType(null)}
                  onTouchStart={(e) => handleTouchStart(fieldType.type, e)}
                  onTouchEnd={handleTouchEnd}
                  onClick={() => setDraggedFieldType(fieldType.type)}
                  className={`cursor-pointer touch-manipulation p-2 rounded-lg border-2 border-dashed transition-colors whitespace-nowrap ${
                    draggedFieldType === fieldType.type
                      ? 'border-[#9B1421] bg-[#9B1421]/10'
                      : 'border-[#504A4A] active:border-[#363232] bg-[#363232]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{fieldType.icon}</span>
                    <span className="text-[#EBFFEE] font-medium text-sm">{fieldType.label}</span>
                  </div>
                </div>
              ))}
            </div>
            {draggedFieldType && (
              <div className="mt-2 text-xs text-[#A9A3A3] text-center">
                Tap on the PDF to place: <span className="font-semibold text-[#EBFFEE]">{FIELD_TYPES.find(ft => ft.type === draggedFieldType)?.label}</span>
              </div>
            )}
          </div>

          {/* Desktop: Left Sidebar - Field Types */}
          <div className="hidden md:flex w-64 bg-[#221F1F] border-r border-[#504A4A] overflow-y-auto p-4 shrink-0 flex-col">
            <h2 className="text-lg font-semibold text-[#EBFFEE] mb-4">Field Types</h2>
            <div className="space-y-2">
              {FIELD_TYPES.map((fieldType) => (
                <div
                  key={fieldType.type}
                  draggable
                  onDragStart={() => setDraggedFieldType(fieldType.type)}
                  onDragEnd={() => setDraggedFieldType(null)}
                  className={`cursor-move p-3 rounded-lg border-2 border-dashed transition-colors ${
                    draggedFieldType === fieldType.type
                      ? 'border-[#9B1421] bg-[#9B1421]/10'
                      : 'border-[#504A4A] hover:border-[#363232] bg-[#363232]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{fieldType.icon}</span>
                    <div>
                      <div className="text-[#EBFFEE] font-medium">{fieldType.label}</div>
                      {fieldType.defaultAutoFill && (
                        <div className="text-xs text-[#A9A3A3]">Auto-fills</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-[#504A4A]">
              <h3 className="text-sm font-semibold text-[#A9A3A3] mb-2">Instructions</h3>
              <p className="text-xs text-[#A9A3A3]">
                1. Drag a field type onto the PDF<br />
                2. Click to select and position<br />
                3. Drag corners to resize<br />
                4. Configure auto-fill in panel
              </p>
            </div>
          </div>

          {/* PDF Canvas Container - Responsive and scrollable */}
          <div ref={scrollContainerRef} className="flex-1 overflow-auto bg-[#363232] p-2 md:p-8 overscroll-contain">
            <div style={{ 
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              minHeight: '100%',
              width: 'max-content',
              minWidth: '100%',
            }}>
              <div style={{ 
                width: `${pdfPageDimensions.width * scale}px`,
                height: `${pdfPageDimensions.height * scale}px`,
                flexShrink: 0,
              }}>
                {/* Container that scales both PDF and fields together - explicit dimensions match PDF */}
                <div
                  ref={canvasRef}
                  onClick={handleCanvasClick}
                  onDragOver={handleCanvasDragOver}
                  onDrop={handleCanvasDrop}
                  onDragLeave={() => setIsDraggingOnCanvas(false)}
                  onTouchEnd={handleCanvasTouchEnd}
                  className={`relative bg-white shadow-2xl touch-none ${isDraggingOnCanvas ? 'ring-2 ring-[#9B1421] ring-opacity-50' : ''}`}
                  style={{ 
                    transform: `scale(${scale})`, 
                    transformOrigin: 'top left',
                    width: `${pdfPageDimensions.width}px`,
                    height: `${pdfPageDimensions.height}px`,
                  }}
                >
              {pdfUrl ? (
                <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={(error) => {
                      console.error('PDF load error:', error);
                      showNotification('error', `Failed to load PDF: ${error.message || 'Unknown error'}`);
                    }}
                    loading={
                      <div className="flex items-center justify-center min-h-[600px] w-full bg-white">
                        <div className="text-[#363232]">Loading PDF...</div>
                      </div>
                    }
                    error={
                      <div className="flex flex-col items-center justify-center min-h-[600px] w-full bg-white">
                        <div className="text-rollops-accent mb-2">Failed to load PDF</div>
                        <div className="text-sm text-[#A9A3A3]">Check console for details</div>
                        <Button
                          onClick={() => {
                            if (template?.storage_path) {
                              getWaiverFileUrl(template.storage_path, 3600)
                                .then(setPdfUrl)
                                .catch(console.error);
                            }
                          }}
                          className="mt-4 bg-[#9B1421] hover:bg-[#7A0F1A] text-white px-4 py-2"
                        >
                          Retry
                        </Button>
                      </div>
                    }
                    options={pdfOptions}
                  >
                    <div className="relative" style={{ width: `${pdfPageDimensions.width}px`, height: `${pdfPageDimensions.height}px` }}>
                      <Page
                        pageNumber={pageNumber}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        width={612}
                        onRenderSuccess={onPageRenderSuccess}
                        onLoadError={(error) => {
                          console.error('Page load error:', error);
                        }}
                        className="!m-0 !p-0"
                      />
                      
                      {/* Fields overlay - positioned relative to PDF page with exact dimensions */}
                      <div 
                        className="fields-overlay-debug absolute top-0 left-0" 
                        style={{ 
                          width: `${pdfPageDimensions.width}px`, 
                          height: `${pdfPageDimensions.height}px`, 
                          pointerEvents: 'none',
                          zIndex: 10,
                          margin: 0,
                          padding: 0,
                        }}
                      >
                    {fields.map((field, index) => {
                      const isSelected = selectedField === field || (field.id && selectedField?.id === field.id);
                      // CRITICAL: Fields must render at the same visual position as mobile
                      // Mobile: field.x * actualScale where actualScale = baseScale * pdfScale, baseScale = screenWidth / 612
                      // Web: Container is 612px wide, scaled by CSS transform
                      // Fields overlay is INSIDE the scaled container, so fields render at field.x pixels
                      // The container's CSS transform scales everything together automatically
                      // This ensures fields scale and move with the PDF correctly
                      // 
                      // ADJUSTMENT: Adding 10px to Y coordinate to match mobile rendering
                      // Mobile is the source of truth; web was rendering fields ~10px too high
                      const fieldLeft = field.x;
                      const fieldTop = field.y + 10;
                      const fieldWidth = field.width;
                      const fieldHeight = field.height;
                      
                      return (
                        <div
                          key={field.id || index}
                          data-field-index={index}
                          onMouseDown={(e) => handleFieldDrag(field, e)}
                          onTouchStart={(e) => handleFieldTouchStart(field, e)}
                          className={`absolute border-2 cursor-move touch-manipulation group ${
                            isSelected && !field.optional
                              ? 'border-[#9B1421] bg-[#9B1421]/10'
                              : isSelected && field.optional
                              ? 'border-yellow-500 bg-yellow-500/20'
                              : field.optional
                              ? 'border-yellow-500 bg-yellow-500/10 hover:border-yellow-400 active:border-yellow-300'
                              : 'border-[#504A4A] bg-[#363232]/50 hover:border-[#9B1421] active:border-[#9B1421]'
                          }`}
                          style={{
                            left: `${fieldLeft}px`,
                            top: `${fieldTop}px`,
                            width: `${fieldWidth}px`,
                            height: `${fieldHeight}px`,
                            pointerEvents: 'auto',
                          }}
                        >
                          {/* Field label - always visible inside the field box */}
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <span className={`text-xs font-medium px-1 truncate max-w-full ${
                              field.optional 
                                ? 'text-yellow-600' 
                                : 'text-[#363232]'
                            }`}>
                              {field.meta?.label || FIELD_TYPES.find(ft => ft.type === field.field_type)?.label || field.field_type}
                              {field.optional && ' (Optional)'}
                            </span>
                          </div>
                          {isSelected && (
                            <>
                              {/* Resize handles - larger and more visible */}
                              {['nw', 'ne', 'sw', 'se'].map((dir) => (
                                <div
                                  key={dir}
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleFieldResize(field, dir, e);
                                  }}
                                  className={`absolute w-3 h-3 bg-[#9B1421] border-2 border-white rounded-sm shadow-md hover:bg-[#7A0F1A] hover:scale-110 transition-all z-20 ${
                                    dir === 'nw' ? 'cursor-nw-resize' :
                                    dir === 'ne' ? 'cursor-ne-resize' :
                                    dir === 'sw' ? 'cursor-sw-resize' :
                                    'cursor-se-resize'
                                  }`}
                                  style={{
                                    [dir.includes('n') ? 'top' : 'bottom']: '-6px',
                                    [dir.includes('w') ? 'left' : 'right']: '-6px',
                                  }}
                                  title={`Resize ${dir.toUpperCase()}`}
                                />
                              ))}
                            </>
                          )}
                          {/* Show resize handles on hover even when not selected */}
                          {!isSelected && (
                            <>
                              {['nw', 'ne', 'sw', 'se'].map((dir) => (
                                <div
                                  key={`hover-${dir}`}
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleFieldResize(field, dir, e);
                                  }}
                                  className={`absolute w-3 h-3 bg-[#9B1421]/60 border-2 border-white/80 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity z-20 hover:bg-[#9B1421] hover:opacity-100 ${
                                    dir === 'nw' ? 'cursor-nw-resize' :
                                    dir === 'ne' ? 'cursor-ne-resize' :
                                    dir === 'sw' ? 'cursor-sw-resize' :
                                    'cursor-se-resize'
                                  }`}
                                  style={{
                                    [dir.includes('n') ? 'top' : 'bottom']: '-6px',
                                    [dir.includes('w') ? 'left' : 'right']: '-6px',
                                  }}
                                  title={`Resize ${dir.toUpperCase()}`}
                                />
                              ))}
                            </>
                          )}
                        </div>
                      );
                    })}
                      </div>
                    </div>
                  </Document>
              ) : (
                <div className="flex items-center justify-center min-h-[600px] w-full bg-white">
                  <div className="text-[#363232]">Loading PDF...</div>
                </div>
              )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Field Configuration - Absolute positioned to not affect canvas layout */}
          {selectedField && (
            <div className="hidden md:flex fixed right-0 top-[120px] sm:top-[140px] h-[calc(100vh-120px)] sm:h-[calc(100vh-140px)] w-80 bg-[#221F1F] border-l border-[#504A4A] overflow-y-auto p-4 z-40 flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#EBFFEE]">Field Configuration</h2>
                <button
                  onClick={handleCloseFieldDetails}
                  className="text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                    Field Type
                  </label>
                  <div className="bg-[#504A4A] px-3 py-2 rounded text-[#EBFFEE]">
                    {FIELD_TYPES.find(ft => ft.type === selectedField.field_type)?.label || selectedField.field_type}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                    Auto-Fill Source
                  </label>
                  {selectedField.field_type === 'signature' ? (
                    <div className="bg-[#504A4A] px-3 py-2 rounded text-[#A9A3A3] italic">
                      Signature fields always require user signature (cannot be auto-filled)
                    </div>
                  ) : selectedField.field_type === 'initials' ? (
                    <div className="bg-[#504A4A] px-3 py-2 rounded text-[#A9A3A3] italic">
                      Initials fields always require user input (cannot be auto-filled)
                    </div>
                  ) : (
                    <select
                      value={selectedField.meta?.autoFillSource || ''}
                      onChange={(e) => {
                        const updatedField = {
                          ...selectedField,
                          meta: {
                            ...selectedField.meta,
                            autoFillSource: e.target.value as WaiverTemplateFieldMeta['autoFillSource'],
                          },
                        };
                        setSelectedField(updatedField);
                        setFields(fields.map(f => f === selectedField ? updatedField : f));
                      }}
                      className="w-full bg-[#504A4A] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder:text-[#A9A3A3] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all hover:bg-[#5A5454]"
                    >
                      <option value="">None (Manual Entry)</option>
                      <option value="signer_name">Signer Name</option>
                      <option value="student_name">Student Name</option>
                      <option value="user_email">User Email</option>
                      <option value="user_phone">User Phone</option>
                      <option value="current_date">Current Date</option>
                      <option value="gym_name">Gym Name</option>
                      <option value="gym_address">Gym Address</option>
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                    Label (Optional)
                  </label>
                  <input
                    type="text"
                    value={selectedField.meta?.label || ''}
                    maxLength={100}
                    onChange={(e) => {
                      const updatedField = {
                        ...selectedField,
                        meta: {
                          ...selectedField.meta,
                          label: e.target.value,
                        },
                      };
                      setSelectedField(updatedField);
                      setFields(fields.map(f => f === selectedField ? updatedField : f));
                    }}
                    className="w-full bg-[#504A4A] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder:text-[#A9A3A3] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all hover:bg-[#5A5454]"
                    placeholder="Field label"
                  />
                </div>

                {/* Optional Field Toggle - Only for fields that require input */}
                {(selectedField.field_type === 'signature' || 
                  selectedField.field_type === 'initials' || 
                  selectedField.field_type === 'text' || 
                  selectedField.field_type === 'signer_name' ||
                  selectedField.field_type === 'student_name' ||
                  selectedField.field_type === 'email' || 
                  selectedField.field_type === 'phone' || 
                  selectedField.field_type === 'date') && (
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                      Optional Field
                    </label>
                    <div className="flex items-center justify-between bg-[#504A4A] px-3 py-2 rounded">
                      <div className="flex-1">
                        <p className="text-[#EBFFEE] text-sm">Mark this field as optional</p>
                        <p className="text-[#A9A3A3] text-xs mt-1">Users can skip optional fields during signing</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedField.optional ?? false}
                          onChange={(e) => {
                            const updatedField = {
                              ...selectedField,
                              optional: e.target.checked,
                            };
                            setSelectedField(updatedField);
                            setFields(fields.map(f => f === selectedField ? updatedField : f));
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-[#363232] peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-yellow-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#504A4A] after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500" />
                      </label>
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-[#504A4A]">
                  <Button
                    onClick={() => handleDeleteField(selectedField)}
                    className="w-full bg-[#9B1421] hover:bg-[#7A0F1A] text-white flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Field
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Mobile: Field Configuration Modal */}
          {selectedField && (
            <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
              <div className="w-full bg-[#221F1F] rounded-t-lg max-h-[80vh] overflow-y-auto">
                <div className="sticky top-0 bg-[#221F1F] border-b border-[#504A4A] p-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-[#EBFFEE]">Field Configuration</h2>
                  <button
                    onClick={handleCloseFieldDetails}
                    className="text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                      Field Type
                    </label>
                    <div className="bg-[#504A4A] px-3 py-2 rounded text-[#EBFFEE]">
                      {FIELD_TYPES.find(ft => ft.type === selectedField.field_type)?.label || selectedField.field_type}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                      Auto-Fill Source
                    </label>
                    {selectedField.field_type === 'signature' ? (
                      <div className="bg-[#504A4A] px-3 py-2 rounded text-[#A9A3A3] italic">
                        Signature fields always require user signature (cannot be auto-filled)
                      </div>
                    ) : selectedField.field_type === 'initials' ? (
                      <div className="bg-[#504A4A] px-3 py-2 rounded text-[#A9A3A3] italic">
                        Initials fields always require user input (cannot be auto-filled)
                      </div>
                    ) : (
                      <select
                        value={selectedField.meta?.autoFillSource || ''}
                        onChange={(e) => {
                          const updatedField = {
                            ...selectedField,
                            meta: {
                              ...selectedField.meta,
                              autoFillSource: e.target.value as WaiverTemplateFieldMeta['autoFillSource'],
                            },
                          };
                          setSelectedField(updatedField);
                          setFields(fields.map(f => f === selectedField ? updatedField : f));
                        }}
                        className="w-full bg-[#504A4A] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder:text-[#A9A3A3] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all hover:bg-[#5A5454]"
                      >
                        <option value="">None (Manual Entry)</option>
                        <option value="signer_name">Signer Name</option>
                        <option value="student_name">Student Name</option>
                        <option value="user_email">User Email</option>
                        <option value="user_phone">User Phone</option>
                        <option value="current_date">Current Date</option>
                        <option value="gym_name">Gym Name</option>
                        <option value="gym_address">Gym Address</option>
                      </select>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                      Label (Optional)
                    </label>
                    <input
                      type="text"
                      value={selectedField.meta?.label || ''}
                      onChange={(e) => {
                        const updatedField = {
                          ...selectedField,
                          meta: {
                            ...selectedField.meta,
                            label: e.target.value,
                          },
                        };
                        setSelectedField(updatedField);
                        setFields(fields.map(f => f === selectedField ? updatedField : f));
                      }}
                      className="w-full bg-[#504A4A] border border-[#504A4A] rounded-lg text-[#EBFFEE] placeholder:text-[#A9A3A3] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#9B1421]/50 transition-all hover:bg-[#5A5454]"
                      placeholder="Field label"
                    />
                  </div>

                  {/* Optional Field Toggle - Only for fields that require input */}
                  {(selectedField.field_type === 'signature' || 
                    selectedField.field_type === 'initials' || 
                    selectedField.field_type === 'text' || 
                    selectedField.field_type === 'signer_name' ||
                    selectedField.field_type === 'student_name' ||
                    selectedField.field_type === 'email' || 
                    selectedField.field_type === 'phone' || 
                    selectedField.field_type === 'date') && (
                    <div>
                      <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                        Optional Field
                      </label>
                      <div className="flex items-center justify-between bg-[#504A4A] px-3 py-2 rounded">
                        <div className="flex-1">
                          <p className="text-[#EBFFEE] text-sm">Mark this field as optional</p>
                          <p className="text-[#A9A3A3] text-xs mt-1">Users can skip optional fields during signing</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedField.optional ?? false}
                            onChange={(e) => {
                              const updatedField = {
                                ...selectedField,
                                optional: e.target.checked,
                              };
                              setSelectedField(updatedField);
                              setFields(fields.map(f => f === selectedField ? updatedField : f));
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-[#363232] peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-yellow-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-[#504A4A] after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500" />
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-[#504A4A]">
                    <Button
                      onClick={() => handleDeleteField(selectedField)}
                      className="w-full bg-[#9B1421] hover:bg-[#7A0F1A] text-white flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Field
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </AuthenticatedLayout>
  );
}
